<template>
  <div
      class="quantity-input"
      :style="{
         border: !disable ? '1px solid ' + containerBorderColor : undefined
      }"
  >

    <div v-if="disable" class="text-center text-weight-medium text-grey-8 leading-none">
      x {{ valueBlur }} {{ measureShow ? measureName : '' }}
    </div>

    <div
        v-else
        :class="controlClass"
        :style="controlStyle"
        class="flex no-wrap justify-between"
    >
      <q-btn
          :icon="ICONS.minusSolid"
          class="--icon-only"
          unelevated
          @click.stop.prevent="onBasketQuantityDown"
          v-bind="btnProps"
          :disable="!allowDec"
      />

      <div
          :class="inputClass"
          class="c-input flex no-wrap items-center"
          v-if="valueState"
      >
        <input
            ref="input"
            v-model="valueBlur"
            :class="inputControlClass"
            :size="(valueBlur+'').length + inputSizeAdd"
            :style="{
                fontSize: inputSize,
                border: 0,
                outline: 'none'
              }"
            class="col text-center"
            maxlength="7"
            type="text"
            @blur="onBlur"
            v-if="!inputDisable"
        />
        <div
            v-else
            :class="inputControlClass"
            :style="{
              fontSize: inputSize,
              border: 0,
            }"
        >
          {{ valueBlur }}
        </div>

        <div
            v-if="measureShow && measureName"
            :style="{
              fontSize: inputSize
            }"
            class="q-mr-xs"
        >
          {{ measureName }}
        </div>
      </div>

      <q-btn
          :icon="ICONS.plusSolid"
          class="--icon-only"
          unelevated
          @click.stop.prevent="onBasketQuantityUp"
          v-bind="btnProps"
          :disable="!allowInc"
      />
    </div>

    <div v-if="error" class="q-mt-sm text-red s-font-sm">
      {{ error }}
    </div>

  </div>
</template>

<script setup lang="ts">

import {ICONS} from "@/assets/icons";
import {computed, ref, toRefs, watch} from "vue";
import {QBtnProps} from "quasar";
import {ComponentProps} from "@/core/vue/types";
import {useDebugComponent} from "@/core/composable/useDebugComponent";

type QuantityInputProps = {
  disable?: boolean
  measureRatio?: number
  measureName?: string
  measureShow?: boolean
  inputSize?: string
  inputSizeAdd?: number
  btn?: ComponentProps<QBtnProps>
  controlClass?: string
  controlStyle?: any
  inputClass?: string
  inputControlClass?: string
  inputDisable?: boolean
  color?: string
  containerBorderColor?: string
  allowInc?: boolean
  allowDec?: boolean
  rounded?: boolean
  minimal?: number
}

const props = withDefaults(defineProps<QuantityInputProps>(), {
  disable: false,
  measureRatio: 1,
  measureShow: false,
  inputSize: '16px',
  inputSizeAdd: 0,
  inputDisable: false,
  color: 'grey-7',
  containerBorderColor: '#EEEEEE',
  btn: () => ({}),
  rounded: true,
  allowInc: true,
  allowDec: true,
})

const {
  measureRatio,
  inputSizeAdd,
  inputDisable,
  controlStyle,
  controlClass,
  color,
  containerBorderColor,
  minimal
} = props

const {btn} = toRefs(props)

const btnProps: QBtnProps = {
  size: '16px',
  outline: true,
  class: 'q-px-sm',
  flat: true,
  dense: true,
  color: color,
  ...btn.value
}

const model = defineModel<any>({default: 0})

const emit = defineEmits<{
  'update:model-value': [number]
}>()

const measureRatioDights = computed(() => {
  const ratio = measureRatio || 1
  if (Math.floor(ratio) === ratio) return 0
  // @ts-ignore
  return ratio.toString().split(".")[1].length || 0
})

const normalizeValue = (val: string | number) => {
  return Number((typeof val === 'string' ? parseFloat(val) : val).toFixed(measureRatioDights.value))
}

const val = normalizeValue(model.value || measureRatio || 0)

const valueState = ref<number>(val)
const valueBlur = ref<string>(val.toString())
const error = ref<any>()

const valueInput = computed({
  get: () => {
    return valueState.value ? normalizeValue(valueState.value) : 0
  },
  set: (val: string | number) => {
    valueState.value = normalizeValue(val)
    model.value = valueState.value
  },
})

const onBlur = () => {
  if (parseFloat(valueBlur.value) === 0) {
    error.value = null
    valueInput.value = 0
    return
  }
  if (!error.value)
    valueInput.value = valueBlur.value
}

const onBasketQuantityDown = () => {
  let newValue = valueState.value - measureRatio
  if (minimal && newValue < minimal) {
    newValue = minimal
  } else if (newValue < 0) {
    newValue = 0
  }
  emit('update:model-value', newValue)
}

const onBasketQuantityUp = () => {
  const newValue = valueState.value + measureRatio
  emit('update:model-value', newValue)
}

watch(valueBlur, () => {
  error.value = ''
})

watch(model, () => {
  const val = normalizeValue(model.value || 0)
  valueState.value = val
  valueBlur.value = val.toString()
})

// Debug композабл для отслеживания ререндеров
useDebugComponent('QuantityInput', { collectRenders: true });

</script>

<style lang="scss" scoped>

.quantity-input {
  border-radius: 18px;
  overflow: hidden;
}

.c-input {
  min-height: 100%;

  .bordered {
    border-top: 1px solid #2C2C75FF;
    border-bottom: 1px solid #2C2C75FF;
    margin: 0 -2px 0 -2px;
    padding: 0 3px;
  }

  input {
    padding: 0;
  }
}

.c-action-tooltip {
  max-width: 150px;
}


</style>
